import {
  HHAlert,
  HHStack,
  HHToggleButton,
  HHToggleButtonGroup,
} from '@hinge-health/react-component-library';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackLink } from '../components/back-link';
import {
  ConfigurationForm,
  ConfigurationFormValues,
} from '../components/configuration-form';
import { ConfigurationJsonPasteForm } from '../components/configuration-json-paste-form';
import { PageHeader } from '../components/page-header';
import { SurfaceCard } from '../components/surface-card';
import {
  buildConfigurationDetailPath,
  CONFIGURATIONS_BACK_LINK_LABEL,
  PARTNER_DATA_GATEWAY_ROUTES,
} from '../constants/partner-data-gateway.constant';
import { useBasePath } from '../context/base-path-context';
import { usePermissions } from '../context/permissions-context';
import { useActionsCatalog } from '../hooks/use-actions-catalog';
import { useCreateConfiguration } from '../hooks/use-configuration-mutations';
import { useDataProviders } from '../hooks/use-data-providers';

export const CONFIGURATION_CREATE_PAGE_TEST_ID =
  'partner-data-gateway-configuration-create-page';
export const CONFIGURATION_CREATE_NO_ACCESS_TEST_ID =
  'partner-data-gateway-configuration-create-no-access';
export const CONFIGURATION_CREATE_BACK_LINK_TEST_ID =
  'partner-data-gateway-configuration-create-back-link';

export const CONFIGURATION_CREATE_MODE_TEST_ID =
  'partner-data-gateway-configuration-create-mode';
export const CONFIGURATION_CREATE_MODE_LABEL = 'How to create';
export const CONFIGURATION_CREATE_MODE_FORM_LABEL = 'Form';
export const CONFIGURATION_CREATE_MODE_PASTE_LABEL = 'Paste JSON';

type CreateMode = 'form' | 'paste';

export const CONFIGURATION_CREATE_NO_ACCESS_MESSAGE =
  'You do not have access to create partner data configurations.';

/**
 * Create view for a new configuration.
 *
 * On success we navigate straight to the new configuration's detail page:
 * a freshly created configuration is a DRAFT, and the operator's next step
 * is almost always to review it and publish.
 *
 * Two ways in: the form, or pasting a whole configuration file (e.g. a
 * reviewed file from `partner-data-gateway-service/configurations/`). Both
 * submit through the same mutation and land on the same DRAFT detail page.
 */
export const ConfigurationCreatePage = (): JSX.Element => {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const { canManageConfigurations } = usePermissions();

  const { actionTypes } = useActionsCatalog();
  const { dataProviders } = useDataProviders();

  const { createConfiguration, isSaving, error } = useCreateConfiguration();
  const [mode, setMode] = useState<CreateMode>('form');

  const configurationsPath = `${basePath}/${PARTNER_DATA_GATEWAY_ROUTES.configurations}`;

  const handleSubmit = async (
    values: ConfigurationFormValues,
  ): Promise<void> => {
    try {
      const created = await createConfiguration(values);

      navigate(`${basePath}/${buildConfigurationDetailPath(created.id)}`);
    } catch {
      // Surfaced through `error` on the form.
    }
  };

  if (!canManageConfigurations) {
    return (
      <HHAlert hhVariant="outlined" severity="warning">
        <span data-testid={CONFIGURATION_CREATE_NO_ACCESS_TEST_ID}>
          {CONFIGURATION_CREATE_NO_ACCESS_MESSAGE}
        </span>
      </HHAlert>
    );
  }

  return (
    <HHStack
      flex={1}
      direction="column"
      gap={4}
      paddingX={6}
      paddingY={6}
      data-testid={CONFIGURATION_CREATE_PAGE_TEST_ID}
    >
      <BackLink
        to={configurationsPath}
        label={CONFIGURATIONS_BACK_LINK_LABEL}
        testId={CONFIGURATION_CREATE_BACK_LINK_TEST_ID}
      />

      <PageHeader
        title="Create configuration"
        subtitle="New configurations start as drafts. Review, then publish."
      />

      <HHToggleButtonGroup
        size="small"
        aria-label={CONFIGURATION_CREATE_MODE_LABEL}
        data-testid={CONFIGURATION_CREATE_MODE_TEST_ID}
        sx={{ alignSelf: 'flex-start' }}
      >
        <HHToggleButton
          hhVariant="variant-bypass"
          value="form"
          selected={mode === 'form'}
          disabled={isSaving}
          onClick={(): void => setMode('form')}
          sx={{ borderRadius: '4px 0 0 4px', textTransform: 'none' }}
        >
          {CONFIGURATION_CREATE_MODE_FORM_LABEL}
        </HHToggleButton>
        <HHToggleButton
          hhVariant="variant-bypass"
          value="paste"
          selected={mode === 'paste'}
          disabled={isSaving}
          onClick={(): void => setMode('paste')}
          sx={{ borderRadius: '0 4px 4px 0', textTransform: 'none' }}
        >
          {CONFIGURATION_CREATE_MODE_PASTE_LABEL}
        </HHToggleButton>
      </HHToggleButtonGroup>

      <SurfaceCard padding={6}>
        {mode === 'form' ? (
          <ConfigurationForm
            dataProviders={dataProviders}
            actionTypes={actionTypes}
            isSaving={isSaving}
            error={error}
            submitLabel="Create configuration"
            onCancel={(): void => navigate(configurationsPath)}
            onSubmit={(values): void => {
              handleSubmit(values);
            }}
          />
        ) : (
          <ConfigurationJsonPasteForm
            isSaving={isSaving}
            error={error}
            onCancel={(): void => navigate(configurationsPath)}
            onSubmit={(values): void => {
              handleSubmit(values);
            }}
          />
        )}
      </SurfaceCard>
    </HHStack>
  );
};

export default ConfigurationCreatePage;
